import { keepPreviousData } from '@tanstack/react-query';
import type { SortingState } from '@tanstack/react-table';
import type { PaginationProps } from 'antd';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { useLoadViewState } from '../common/cachedViewState/useLoadViewState';
import {
  deserializeValidPeriod,
  serializeSort,
  serializeValidPeriod,
  useDirectory,
  useSaveDirectoryState,
} from '../common/directory';
import { EMPTY_PAGE } from '../common/queryClient';
import {
  DataObjectDirectorySortField,
  DataObjectFilterDto,
  DataObjectView,
} from './types';
import {
  CustomPagination,
  DirectoryHeader,
  InfoIconUrl,
  Main,
  Padding,
  Stack,
  TotalEntries,
} from '../common/components';
import { DataObjectTable } from './DataObjectTable';
import { DataObjectFilter } from './DataObjectFilter';
import { useListDataObjectsQuery } from './query/useListDataObjectsQuery';
import { FormInstance } from 'antd/es/form/hooks/useForm';
import { DataObjectDirectorySchema } from './schema/DataObjectDirectorySchema';
import { getValuesFromString } from './utils';

export interface DataObjectDirectoryProps {
  title: string | null;
  dataObjectView: DataObjectView;
  getRedirectAnchor: (versionId: number, value: string) => React.ReactNode;
  onClickCreate: () => void;
  onSelectVersion: (params: { id?: number }) => void;
}

const FILTER_KEYS: (keyof DataObjectFilterDto)[] = [
  'confidentialityStatusIds',
  'dataObjectType',
  'dataProviderGroupIds',
  'domainIds',
  'frequencyIds',
  'groupIds',
  'accessGroupIds',
  'indicatorStatus',
  'originIds',
  'outputTypeCodes',
  'recipientIds',
  'search',
  'sourceIds',
  'statusCodes',
  'outputs',
  'primaryOutputIds',
  'subgroupIds',
  'validFrom',
  'validTo',
];
const NUM_FIELDS: (keyof DataObjectFilterDto)[] = [
  'confidentialityStatusIds',
  'dataProviderGroupIds',
  'domainIds',
  'frequencyIds',
  'groupIds',
  'accessGroupIds',
  'recipientIds',
  'originIds',
  'sourceIds',
  'outputs',
  'primaryOutputIds',
  'subgroupIds',
];
const ARRAY_FIELDS: (keyof DataObjectFilterDto)[] = [
  ...NUM_FIELDS,
  'outputTypeCodes',
  'statusCodes',
];
const BOOLEAN_FIELDS: (keyof DataObjectFilterDto)[] = ['indicatorStatus'];

export const DataObjectDirectoryView: FC<DataObjectDirectoryProps> = (
  props,
) => {
  const stateStorageKey = props.dataObjectView.isInput
    ? 'inputDataObjectDirectory'
    : 'outputDataObjectDirectory';

  const { state: viewState, isQueryParamCleared } = useLoadViewState(
    stateStorageKey,
    {
      filters: { statusCodes: ['ACTIVE'] },
      sort: [{ id: 'code', desc: false }],
    },
  );

  if (!isQueryParamCleared) {
    return null;
  }

  return (
    <DataObjectDirectoryViewInner
      {...props}
      initialFilters={viewState.filters}
      initialSort={viewState.sort}
      initialPagination={viewState.pagination}
      stateStorageKey={stateStorageKey}
    />
  );
};

interface DataObjectDirectoryViewInnerProps extends DataObjectDirectoryProps {
  initialFilters: Partial<Record<string, any>>;
  initialSort: SortingState;
  initialPagination: PaginationProps | undefined;
  stateStorageKey: string;
}

export const DataObjectDirectoryViewInner: FC<
  DataObjectDirectoryViewInnerProps
> = (props) => {
  const {
    title,
    dataObjectView,
    getRedirectAnchor,
    onClickCreate,
    onSelectVersion,
    initialFilters,
    initialSort,
    initialPagination,
    stateStorageKey,
  } = props;

  const formRef = useRef<FormInstance>(null);

  const {
    headerProps,
    tableProps,
    sidePanelFilterProps,
    paginationProps,
    queryParameters,
    setTotal,
  } = useDirectory({
    queryFilterKeys: FILTER_KEYS,
    arrayFields: ARRAY_FIELDS,
    numericFields: NUM_FIELDS,
    booleanFields: BOOLEAN_FIELDS,
    initialFilters,
    sidePanelFilterRef: formRef,
    initialSort,
    initialPagination,
    serializeFilter: (id, value) => {
      // groupAndSubgroupIds -> groupIds, subgroupIds
      if (id === 'groupAndSubgroupIds' && Array.isArray(value)) {
        return {
          groupIds: getValuesFromString(
            value.filter((item) => item.includes('g-')),
          ),
          subgroupIds: getValuesFromString(
            value.filter((item) => item.includes('s-')),
          ),
        };
      }
      // validPeriod -> validFrom, validTo
      return serializeValidPeriod(id, value);
    },
    deserializeFilter: (id, value, allQueryFilters) => {
      // groupIds, subgroupIds -> groupAndSubgroupIds
      if (id === 'groupIds' || id === 'subgroupIds') {
        const { groupIds = [], subgroupIds = [] } = allQueryFilters;
        return {
          groupAndSubgroupIds:
            groupIds.length || subgroupIds.length
              ? [...groupIds, ...subgroupIds]
              : undefined,
        };
      }
      // validFrom, validTo -> validPeriod
      return deserializeValidPeriod(id, value, allQueryFilters);
    },
    serializeSort: ({ id, desc }) =>
      serializeSort<DataObjectDirectorySortField>({
        id:
          id === 'frequencyIds'
            ? 'frequencyOrder'
            : id === 'originIds'
              ? 'sourceNames'
              : id,
        desc,
      }),
  });

  useSaveDirectoryState(stateStorageKey, {
    paginationProps,
    queryParameters,
    tableProps,
  });

  const [isFiltersOpen, setIsFiltersOpen] = useState<boolean>(false);

  const {
    data: { content: dataObjects = [], totalElements } = EMPTY_PAGE,
    isFetching: isDataObjectsLoading,
  } = useListDataObjectsQuery(
    {
      ...queryParameters,
      dataObjectType: dataObjectView.dataObjectType,
    },
    {
      placeholderData: keepPreviousData,
    },
  );

  useEffect(() => {
    setTotal(totalElements);
  }, [setTotal, totalElements]);

  const dataSource = useMemo(
    () => DataObjectDirectorySchema.array().parse(dataObjects),
    [dataObjects],
  );

  const infoIconUrl = props.dataObjectView.isInput
    ? InfoIconUrl.INPUT_DATA_OBJECT_DESCRIPTIONS_DIRECTORY
    : InfoIconUrl.OUTPUT_DATA_OBJECT_DESCRIPTIONS_DIRECTORY;

  return (
    <Main>
      <Main.Col border={false}>
        <Main.Content padding={false} id="classifier-mapping-dir-content">
          <ContentWrapper>
            <DirectoryHeader
              {...headerProps}
              title={title}
              infoIconUrl={infoIconUrl}
              isFiltersOpen={isFiltersOpen}
              onFilterToggle={() => setIsFiltersOpen((current) => !current)}
            />
            <Padding horizontal bottom>
              <DataObjectTable
                {...tableProps}
                data={dataSource}
                dataObjectView={dataObjectView}
                getRedirectAnchor={getRedirectAnchor}
                onClickCreate={onClickCreate}
                onSelectVersion={onSelectVersion}
                isLoading={isDataObjectsLoading}
              />
            </Padding>
          </ContentWrapper>
        </Main.Content>
        <Main.Footer>
          <TotalEntries count={totalElements} />
          <FooterPagination {...paginationProps} />
        </Main.Footer>
      </Main.Col>
      {isFiltersOpen && (
        <DataObjectFilter
          dataObjectView={dataObjectView}
          filters={queryParameters}
          formRef={formRef}
          {...sidePanelFilterProps}
          onClose={() => setIsFiltersOpen(false)}
          showToggleCollapseButton={false}
        />
      )}
    </Main>
  );
};

const FooterPagination = styled(CustomPagination)`
  margin-left: auto;
`;

const ContentWrapper = styled(Stack)`
  width: 100%;
  max-width: 110em;
  height: 100%;
  align-self: center;
`;
